import {
  Box,
  FormControl,
  FormErrorMessage,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import type { UseFormReturn } from 'react-hook-form';

import type {
  CreateVoucherRequest,
  ShipToCsvUploadResponse,
} from '../../../types';
import { ShipToDropzone } from '../../ship-to-dropzone';

interface ShipToTargetSectionProps {
  errors: UseFormReturn<CreateVoucherRequest>['formState']['errors'];
  isUploading: boolean;
  csvUploadUrl: string | null;
  csvUploadStats: {
    total_rows: number;
    valid_rows: number;
    invalid_rows: number;
    errors?: ShipToCsvUploadResponse['errors'];
  } | null;
  onDownloadTemplate: () => void;
  onUpload: (files: File[]) => void;
  onRemove: () => void;
  disabled?: boolean;
}

export const ShipToTargetSection = ({
  errors,
  isUploading,
  csvUploadUrl,
  csvUploadStats,
  onDownloadTemplate,
  onUpload,
  onRemove,
  disabled = false,
}: ShipToTargetSectionProps) => (
  <VStack spacing={3} align="stretch">
    <FormControl isInvalid={!!errors.shipToCsv}>
      <Text fontSize="sm" fontWeight="medium" color="gray.700">
        1. Download template ship to csv{' '}
        <Link
          color="blue.500"
          onClick={disabled ? undefined : onDownloadTemplate}
          cursor={disabled ? 'not-allowed' : 'pointer'}
          opacity={disabled ? 0.6 : 1}
        >
          disini
        </Link>
      </Text>
      <Text fontSize="sm" fontWeight="medium" color="gray.700" mt={2}>
        2. Upload template ship to csv
      </Text>
      <Text fontSize="xs" color="gray.500">
        Maksimal ukuran file 2MB per upload
      </Text>
      <ShipToDropzone
        name="shipToCsv"
        errors={errors}
        isUploading={isUploading}
        isDisabled={disabled}
        onUpload={onUpload}
        onRemove={onRemove}
      />
      <FormErrorMessage>{errors.shipToCsv?.message as string}</FormErrorMessage>
    </FormControl>

    {csvUploadUrl && (
      <Text fontSize="sm" color="gray.700">
        3. Download list ship to csv{' '}
        <Link
          href={csvUploadUrl}
          color="blue"
          download
          target="_blank"
          rel="noopener noreferrer"
        >
          disini
        </Link>
      </Text>
    )}

    {csvUploadStats && csvUploadUrl && (
      <VStack spacing={1} align="start" fontSize="xs" color="gray.600">
        <Text>
          Total rows: <strong>{csvUploadStats.total_rows}</strong>
        </Text>
        <Text color="green.600">
          Valid rows: <strong>{csvUploadStats.valid_rows}</strong>
        </Text>
        {csvUploadStats.invalid_rows > 0 && (
          <Text color="orange.600">
            Invalid rows: <strong>{csvUploadStats.invalid_rows}</strong>
          </Text>
        )}
        {csvUploadStats.invalid_rows > 0 &&
          csvUploadStats.errors &&
          csvUploadStats.errors.length > 0 && (
            <Box
              maxH="200px"
              overflowY="auto"
              w="100%"
              mt={1}
              p={2}
              borderWidth="1px"
              borderColor="orange.200"
              borderRadius="md"
              bg="orange.50"
            >
              <VStack spacing={0.5} align="start">
                {csvUploadStats.errors.map((error, index) => (
                  <Text key={index} color="orange.700">
                    {error.message}
                  </Text>
                ))}
              </VStack>
            </Box>
          )}
      </VStack>
    )}
  </VStack>
);
